import { PerformancePanel } from "../common/PerformanceWorkspace";
import { formatCurrency, formatPercent, splitTicketGroups } from "../common/performanceFormat";

// Abaixo de meio por cento a diferença é arredondamento, e "0,2% acima" só confunde.
const SAME_AS_TEAM_THRESHOLD = 0.5;

/**
 * Diferença do ticket do colaborador para o da equipe, em percentual do ticket da equipe.
 * Nula quando a equipe não vendeu o grupo e não há base para comparar.
 */
export const ticketDifference = (employeeTicket, teamTicket) => {
    const team = Number(teamTicket) || 0;
    if (team <= 0) {
        return null;
    }
    return (((Number(employeeTicket) || 0) - team) / team) * 100;
};

/** "71,4% acima da equipe", "30,0% abaixo da equipe" ou "Igual à equipe", com a cor do sinal. */
const DifferenceNote = ({ difference }) => {
    if (difference == null) {
        return null;
    }
    if (Math.abs(difference) < SAME_AS_TEAM_THRESHOLD) {
        return <span className="employee-analysis__ticket-diff employee-analysis__ticket-diff--same">Igual à equipe</span>;
    }
    const up = difference > 0;
    return (
        <span className={`employee-analysis__ticket-diff ${up ? "employee-analysis__diff--up" : "employee-analysis__diff--down"}`}>
            {formatPercent(Math.abs(difference))} {up ? "acima" : "abaixo"} da equipe
        </span>
    );
};

/**
 * Uma barra com o valor ao lado. A escala é a da própria linha (o maior entre colaborador e
 * equipe): serviços e uma categoria pequena têm ordens de grandeza diferentes, e uma escala única
 * deixaria as categorias pequenas sem barra visível.
 */
const TicketBar = ({ value, maximum, color, children }) => (
    <div className="employee-analysis__ticket-bar-line">
        <div className="employee-analysis__ticket-track" aria-hidden="true">
            <div
                className="employee-analysis__ticket-bar"
                style={{ width: `${maximum > 0 ? (value / maximum) * 100 : 0}%`, backgroundColor: color }}
            />
        </div>
        {children}
    </div>
);

/** Linha de um grupo: nome, diferença para a equipe, as duas barras e a presença nas comandas. */
const CategoryTicketRow = ({ group, colors }) => {
    const employeeTicket = Number(group.employee.ticket) || 0;
    const teamTicket = Number(group.team.ticket) || 0;
    const maximum = Math.max(employeeTicket, teamTicket);

    return (
        <li className="employee-analysis__ticket-row">
            <div className="employee-analysis__ticket-head">
                <span>{group.label}</span>
                <DifferenceNote difference={ticketDifference(employeeTicket, teamTicket)} />
            </div>
            <TicketBar value={employeeTicket} maximum={maximum} color={colors.line}>
                <strong>{formatCurrency(employeeTicket)}</strong>
            </TicketBar>
            <TicketBar value={teamTicket} maximum={maximum} color={colors.subscription}>
                <span>{formatCurrency(teamTicket)}</span>
            </TicketBar>
            <small>
                Presente em {formatPercent(group.employee.share)} das comandas (equipe {formatPercent(group.team.share)})
            </small>
        </li>
    );
};

/**
 * Seção "Ticket por categoria" da análise individual: quanto cada atendimento do colaborador gera
 * em serviços, produtos e cada categoria, ao lado da equipe que atendeu no mesmo mês e filial. É o
 * detalhe do quadro da equipe no painel, que não lista ninguém individualmente.
 *
 * @param {{groups: Array, employeeName: string, colors: {line: string, subscription: string}}} props
 *        grupos de analysis.categoryTickets, nome curto do colaborador e as cores do gráfico de
 *        atendimentos, para colaborador e equipe terem as mesmas cores da tela inteira
 */
const CategoryTicketComparison = ({ groups, employeeName, colors }) => {
    const { types, categories } = splitTicketGroups(groups);

    return (
        <PerformancePanel
            title="Ticket por categoria"
            subtitle="Quanto cada atendimento gera, em média, em cada categoria, comparado com a equipe que atendeu no mesmo mês e filial."
            className="mb-4 employee-analysis__section"
        >
            <div className="employee-analysis__legend" aria-hidden="true">
                <span><i className="employee-analysis__key employee-analysis__key--block" style={{ backgroundColor: colors.line }}></i> {employeeName}</span>
                <span><i className="employee-analysis__key employee-analysis__key--block" style={{ backgroundColor: colors.subscription }}></i> Equipe</span>
            </div>
            <ul className="employee-analysis__ticket-list">
                {types.map((group) => <CategoryTicketRow key={group.key} group={group} colors={colors} />)}
            </ul>
            {categories.length > 0 && (
                <>
                    <h6 className="employee-analysis__ticket-heading">Por categoria</h6>
                    <ul className="employee-analysis__ticket-list">
                        {categories.map((group) => <CategoryTicketRow key={group.key} group={group} colors={colors} />)}
                    </ul>
                </>
            )}
        </PerformancePanel>
    );
};

export default CategoryTicketComparison;
